import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_LOGOUT_EVENT_KEY } from '../constants/auth-storage.constants';
import { clearStoredSession } from '../utils/auth-session-storage';
import { sanitizeReturnUrl } from '../utils/return-url';

export const SESSION_EXPIRED_REASON = 'session-expired';

// Single place where a session is closed in this tab: clear the stored session, tell the other
// tabs through the logout event and go to the login page. Resetting the in-memory state is the
// reducer's job.
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly router = inject(Router);

  // The user chose to leave. The login page gets no return URL and no reason.
  logout(): void {
    // A future `POST /auth/logout` call goes here: fire it with the current token before the
    // cleanup below and do not wait for it, so signing out never depends on the network.
    this.closeSession();
    this.router.navigate(['/login']);
  }

  // The refresh token was rejected for good: remember where the user was so signing in again
  // can bring them back, and tell the login page why they are there.
  expire(returnUrl: string): void {
    const safeReturnUrl = sanitizeReturnUrl(returnUrl);

    this.closeSession();
    this.router.navigate(['/login'], {
      queryParams: {
        ...(safeReturnUrl ? { returnUrl: safeReturnUrl } : {}),
        reason: SESSION_EXPIRED_REASON,
      },
    });
  }

  private closeSession(): void {
    clearStoredSession();
    localStorage.setItem(AUTH_LOGOUT_EVENT_KEY, String(Date.now()));
  }
}
